import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Upload, Save, Loader2, Trash2, LayoutGrid } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { invalidateSettingsCache } from '../../lib/settingsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOT_COUNT = 4;
const emptySlot = () => ({ image_url: '', title: '', link: '' });

/**
 * Admin editor for the home page FEATURING widget.
 * Fixed at 4 slots. A slot with no image is simply hidden on the public site,
 * and the remaining tiles stay centred.
 */
const AdminFeaturedWidget = () => {
  const [heading, setHeading] = useState('FEATURING');
  const [slots, setSlots] = useState(Array.from({ length: SLOT_COUNT }, emptySlot));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const fileRefs = useRef([]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin-settings/featured-widget`, { headers: authHeaders() });
        setHeading(data?.heading || 'FEATURING');
        const incoming = Array.from({ length: SLOT_COUNT }, (_, i) => ({
          ...emptySlot(),
          ...((data?.slots || [])[i] || {}),
        }));
        setSlots(incoming);
      } catch (error) {
        toast({
          title: 'Load failed',
          description: error.response?.data?.detail || 'Could not load the Featuring widget.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateSlot = (index, patch) => {
    setSlots((prev) => prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)));
  };

  const uploadImage = async (index, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image.', variant: 'destructive' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: 'Too large', description: 'Images must be 20MB or smaller.', variant: 'destructive' });
      return;
    }

    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'featured-widget');
      const { data } = await axios.post(`${API}/storage/upload`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (!data?.url) throw new Error('Upload response missing URL');
      updateSlot(index, { image_url: data.url });
      toast({ title: 'Uploaded', description: `Slot ${index + 1} image ready. Remember to save.` });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Could not upload the image.',
        variant: 'destructive',
      });
    } finally {
      setUploadingIndex(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin-settings/featured-widget`,
        { heading, slots },
        { headers: authHeaders() },
      );
      invalidateSettingsCache(`${process.env.REACT_APP_BACKEND_URL}/api/settings/featured-widget`);
      toast({ title: 'Saved', description: 'The Featuring widget is live on the home page.' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save the widget.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const filledCount = slots.filter((s) => s.image_url).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="featured-widget-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-featured-widget">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <LayoutGrid className="w-7 h-7 text-blue-600" />
            Featuring Widget
          </h2>
          <p className="text-gray-500 mt-1">
            Four slots on the home page. Empty slots are hidden and the rest stay centred
            ({filledCount} of {SLOT_COUNT} in use). Portrait images work best (4:5, around 500&times;625).
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="featured-widget-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Widget
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Section Heading</CardTitle>
          <CardDescription>Shown above the tiles on the home page.</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="FEATURING"
            data-testid="featured-widget-heading"
          />
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {slots.map((slot, index) => (
          <Card key={index} data-testid={`featured-slot-editor-${index + 1}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-blue-600" />
                  Slot {index + 1}
                </CardTitle>
                {slot.image_url && (
                  <button
                    onClick={() => updateSlot(index, emptySlot())}
                    className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                    data-testid={`featured-slot-clear-${index + 1}`}
                  >
                    <Trash2 className="w-4 h-4" /> Clear
                  </button>
                )}
              </div>
              <CardDescription>{slot.image_url ? 'Visible on the home page' : 'Empty - hidden on the home page'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aspect-[4/5] w-full max-w-[220px] mx-auto rounded-xl border border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center">
                {slot.image_url ? (
                  <img
                    src={resolveMediaUrl(slot.image_url)}
                    alt={slot.title || `Slot ${index + 1}`}
                    className="w-full h-full object-cover"
                    data-testid={`featured-slot-preview-${index + 1}`}
                  />
                ) : (
                  <span className="text-gray-400 text-sm">No image</span>
                )}
              </div>

              <input
                type="file"
                accept="image/*"
                ref={(el) => { fileRefs.current[index] = el; }}
                onChange={(e) => uploadImage(index, e.target.files?.[0])}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileRefs.current[index]?.click()}
                disabled={uploadingIndex === index}
                data-testid={`featured-slot-upload-${index + 1}`}
              >
                {uploadingIndex === index ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {slot.image_url ? 'Replace Image' : 'Upload Image'}
              </Button>

              <div>
                <Label>Title</Label>
                <Input
                  value={slot.title}
                  onChange={(e) => updateSlot(index, { title: e.target.value })}
                  placeholder="e.g. NIGHT KAYAK"
                  className="mt-1"
                  data-testid={`featured-slot-title-${index + 1}`}
                />
              </div>

              <div>
                <Label>Link</Label>
                <Input
                  value={slot.link}
                  onChange={(e) => updateSlot(index, { link: e.target.value })}
                  placeholder="/night-kayak"
                  className="mt-1"
                  data-testid={`featured-slot-link-${index + 1}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminFeaturedWidget;
